import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { fetchLocalizedCollection } from '../lib/cms';

export default function TermsPage() {
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    fetchLocalizedCollection('sider', 'da').then((items) => {
      const getSlugVal = (it) =>
        typeof it?.slug === 'object'
          ? it.slug.da || it.slug._ || it.slug.en || Object.values(it.slug)[0]
          : it?.slug;
      const match = (items || []).find(
        (s) =>
          getSlugVal(s) === 'pmb-salgs-leveringsbetingelser' ||
          getSlugVal(s) === 'terms-and-conditions'
      );
      if (match) setPageData(match);
    });
  }, []);

  const title = pageData?.titel || 'Salgs- og leveringsbetingelser';
  const badge = pageData?.undertitel || 'Juridiske vilkår';
  const seoTitle = pageData?.seo_titel || 'PMB Salgs- & Leveringsbetingelser';
  const metaDesc =
    pageData?.meta_beskrivelse ||
    'Salgs- og leveringsbetingelser for POVL MØLLERS MASKINFABRIK A/S. Opdateret januar 2025.';

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={pageData?.intro || metaDesc} />
      </Helmet>

      {/* Breadcrumb */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="hover:text-[#006368] transition">
            Forside
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">{title}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#1a2024] text-white py-14 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-wider text-[#0d8086] block mb-2">
            {badge}
          </span>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
            {title}
          </h1>
          <p className="mt-2 text-stone-400 text-sm">Opdateret januar 2025</p>
        </div>
      </section>

      {/* Terms Body */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-stone-800 text-sm leading-relaxed space-y-8">
          {pageData?.indhold ? (
            <div
              className="cms-content space-y-6 [&_h2]:text-base [&_h2]:font-bold [&_h2]:text-stone-900 [&_h2]:uppercase [&_h2]:tracking-wide [&_h2]:border-b [&_h2]:border-stone-200 [&_h2]:pb-2 [&_h2]:mt-8 [&_h2]:mb-3 [&_p]:mb-3 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3"
              dangerouslySetInnerHTML={{ __html: pageData.indhold }}
            />
          ) : (
            <>
              <div>
                <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
                  1. GYLDIGHED
                </h2>
            <p className="mb-2">
              1.1 Disse almindelige salgs- og leveringsbetingelser gælder for alle leverancer af produkter, montage og serviceydelser fra Povl Møllers Maskinfabrik A/S, medmindre andet er udtrykkeligt og skriftligt aftalt.
            </p>
            <p>
              1.2 Povl Møllers Maskinfabrik A/S er således ikke bundet af eventuelle fremsatte krav fra købers købsvilkår, selvom Povl Møllers Maskinfabrik ikke har gjort indsigelser mod sådanne vilkår. I øvrigt finder Orgalim S 2022 anvendelse på leverancen, medmindre der samtidig er inkluderet montage, i hvilken tilfælde Orgalim SI 14 finder anvendelse. Er der uoverensstemmelse mellem disse salg- og leveringsbestemmelser er det Orgalims standard betingelser der går forud.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              2. TILBUD
            </h2>
            <p>
              2.1 Tilbud afgives med forbehold for mellemsalg, jf. pkt. 3. Afgiver sælger tilbud, der ikke angiver særlig acceptfrist, bortfalder tilbuddet, såfremt accept ikke er kommet frem til sælger senest 15 dage fra tilbuddets dato.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              3. MELLEMSALG
            </h2>
            <p className="mb-2">
              3.1 Indtil købers accept er kommet frem til sælger, er sælger berettiget til at indgå aftale med tredjemand vedrørende det tilbudte med den virkning, at tilbuddet over for køber bortfalder uden nærmere meddelelse.
            </p>
            <p className="mb-2">
              3.2 Tilbud fra Povl Møllers Maskinfabrik er uforbindende, medmindre andet er angivet i tilbuddet.
            </p>
            <p>
              3.3 Endelig aftaler om leveringer først indgået ved købers modtagelse af Povl Møllers Maskinfabriks ordrebekræftelse, og kun indholdet af denne er bindende for parterne.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              4. PRIS
            </h2>
            <p className="mb-2">
              4.1 Alle priser er i danske kroner eller Euro og eksklusiv moms. Køber er forpligtet til indtil leveringen at acceptere ændringer i prisen som følge af dokumenterede forøgede omkostninger for sælger som følge af ændringer i valutakurser, told, skatter, afgifter m.v. vedrørende den aftalte leverance.
            </p>
            <p>
              4.2 Såfremt det solgte er omfattet af en af sælger benyttet prisliste, sker prisfastsættelsen med udgangspunkt i den prisliste, der er gældende på leveringsdagen.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              5. BETALING
            </h2>
            <p className="mb-2">
              5.1 Betaling skal ske senest den dato, fakturaen angiver som sidste rettidige betalingsdag. Såfremt en sådan ikke er angivet, skal betaling ske kontant ved levering.
            </p>
            <p className="mb-2">
              5.2 Såfremt leveringen udskydes på grund af købers forhold, er køber, med mindre sælger skriftligt meddeler køber andet – alligevel forpligtet til at foretage enhver betaling til sælger, som om levering var sket til aftalt tid.
            </p>
            <p className="mb-2">
              5.3 Hvis betaling sker efter forfaldsdag, er køber pligtig til at svare morarente af det forfaldne beløb med 2% pr. påbegyndt måned.
            </p>
            <p>
              5.4 Køber er ikke berettiget til at modregne med eventuelle modkrav på sælger, som ikke er skriftligt anerkendt af sælger, og har ikke ret til at tilbageholde nogen del af købesummen på grund af modfordringer af nogen art.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              6. EJENDOMSFORBEHOLD
            </h2>
            <p className="mb-2">
              6.1 Sælger forbeholder sig, med de begrænsninger der følger af ufravigelige retsregler, ejendomsretten til det solgte, indtil hele købesummen med tillæg af påløbne omkostninger er betalt til sælger eller til den, han har transporteret sin ret til.
            </p>
            <p>
              6.2 Ved omdannelse eller bearbejdning af det solgte opretholdes ejendomsforbeholdet, således at det omfatter den omdannede eller bearbejdede genstand i et omfang svarende til den værdi, det solgte repræsenterede ved salget.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              7. LEVERING
            </h2>
            <p className="mb-2">
              7.1 Levering sker fra sælgers adresse, uanset om sælger ved egne folk eller ved tredjemand i henhold til særskilt aftale med køber bringer det solgte til køber.
            </p>
            <p className="mb-2">
              7.2 Leveringstidspunktet kan være aftalt til en bestemt dato eller til en bestemt tid efter aftalens indgåelse. Det er en forudsætning, at samtlige oplysninger, der er nødvendige for ordrens udførelse på aftaletidspunktet, er kommet til sælgers kundskab. Leveringen er, når varen er fysisk ankommet til købers adresse. Hvis leveringen er aftalt til ”frit leveret, indbakset og installeret”, skal køber være behjælpeligt og stille nødvendigt personale og hjælpemidler, kran, gaffeltruck samt diverse andet løfteudstyr til rådighed.
            </p>
            <p className="mb-2">
              7.3 Hvis levering ikke sker inden for leveringstiden, er køber alene berettiget til ved skriftlig meddelelse til sælger at kræve levering og fastsætte en endelig rimelig frist herfor, og derved angive, at køber agter at hæve aftalen, såfremt levering ikke sker inden for denne fastsatte frist. Såfremt levering ikke er sket inden for den således af køber fastsatte frist, er køber berettiget til ved skriftlig meddelelse til sælger at hæve aftalen. Såfremt køber vælger at hæve aftalen, er køber ikke berettiget til nogen form for erstatning eller godtgørelse for direkte eller indirekte tab.
            </p>
            <p>
              7.4 Såfremt forsinkelse ved levering skyldes, at sælger er i en situation som angivet i pkt. 12.2, udskydes leveringstiden med den tid, som hindringen varer, idet begge parter dog skal være berettiget til ansvarsfrit at annullere aftalen, når hindringen har varet i mere end 3 måneder. Nærværende bestemmelse finder anvendelse, uanset om årsagen til forsinkelsen indtræffer før eller efter udløbet af den aftalte leveringstid.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              8. FORSENDELSE & 9. EMBALLAGE
            </h2>
            <p className="mb-2">
              8.1 Alle forsendelser, der foretages af sælgeren pr. bil, er betinget af, at aflæsningsstedet er tilgængeligt ad farbar vej, samt aflæsning er som angivet i pkt. 7.2. Køber er ansvarlig for omgående aflæsning. Eventuelt ventetid sker for købers regning.
            </p>
            <p>
              9.1 Emballering hvis maskiner / udstyr kræver en speciel emballering så sker for købers regning, med mindre det udtrykkeligt fremgår, at dette er inkluderet i prisen. Emballagen tages kun retur efter særskilt aftale.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              10. PRODUKTÆNDRINGER & 11. MANGLER OG REKLAMATION
            </h2>
            <p className="mb-2">
              10.1 Sælger forbeholder sig ret til uden varsel at foretage ændringer i aftalte specifikationer, hvis dette kan ske uden ulempe for køber.
            </p>
            <p className="mb-2">
              11.1 Ved levering skal køber straks foretage en sådan undersøgelse af det solgte, som ordentlig forretningsbrug kræver.
            </p>
            <p className="mb-2">
              11.2 Hvis køber vil påberåbe sig en mangel, skal køber, straks efter at manglen er eller burde være opdaget, give sælger skriftlig meddelelse herom samt anføre, hvori manglen består.
            </p>
            <p className="mb-2">
              11.3 Efter sælgers valg vil mangler ved det solgte blive afhjulpet, eller det solgte vil blive omleveret.
            </p>
            <p>
              11.5 Har køber ikke inden 1 måned efter leveringsdato påberåbt sig manglen over for sælger, kan han ikke senere gøre den gældende. For dele, der er udskiftet eller repareret jf. pkt. 11.3, påtager sælger sig de samme forpligtelser, som gælder for det oprindeligt solgte i et tidsrum af 12 måneder, dog således at sælgers mangelansvar ikke for nogen del af det solgte kan udstrækkes til mere end 18 måneder fra den oprindelige leveringsdato.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              12. ANSVARSBEGRÆNSNING VED FORSINKELSE ELLER MANGLER
            </h2>
            <p className="mb-2">
              12.1 Sælger hæfter ikke for driftstab, avancetab eller andre direkte og indirekte tab i anledning af aftalen, herunder indirekte tab, der opstår som følge af forsinkelse eller mangler og defekter ved det solgte.
            </p>
            <p>
              12.2 Force majeure: Arbejdskonflikt, brand, krig, mobilisering, valutarestriktioner, oprør, mangel på transportmidler, almindelig vareknaphed, restriktioner af drivkraft samt mangler ved eller forsinkelse af leverancer fra underleverandører medfører ansvarsfrihed.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              13. GARANTI & 14. BRUGTE MASKINER
            </h2>
            <p className="mb-2">
              13.1 Ved køb af maskiner, produkter og reservedele er garantiperioden 12 måneder ved normal drift på maksimum 8 timer dagligt.
            </p>
            <p className="mb-2">
              13.2 Ved køb af styring og elektriske komponenter er garantiperioden 12 måneder, med forbehold for at der ikke er foretaget indgreb af andre end Povl Møllers Maskinfabrik i elektriske komponenter så vil garantien bortfalde.
            </p>
            <p className="mb-2">
              14.1 Brugte maskiner sælges som nye maskiner og der ydes 12 måneders garanti med betingelser som pkt. 13.1 for renoverede maskiner.
            </p>
            <p>
              14.2 Brugte maskiner som ikke er beskrevet som renoveret er at betragte som en brugt maskine og der ydes 3 måneders garanti medmindre andet er aftalt i Ordrebekræftelsen.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              18. TVISTIGHEDER
            </h2>
            <p>
              18.1 Enhver uoverensstemmelse mellem parterne afgøres ved domstolene efter dansk ret.
            </p>
          </div>

            </>
          )}

          <div className="pt-6 border-t border-stone-200">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-6 py-3 rounded bg-[#006368] text-white font-semibold text-xs uppercase tracking-wider hover:bg-[#004d51] transition"
            >
              <span>Har du spørgsmål til vores betingelser? Kontakt os</span>
              <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
